import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calculator, TrendingUp, TrendingDown, DollarSign, Calendar, FileText, Download, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';

function Contabilidade() {
  const [transacoes, setTransacoes] = useState([]);
  const [relatorioMensal, setRelatorioMensal] = useState([]);
  const [periodoInicio, setPeriodoInicio] = useState(() => {
    // Começa no primeiro dia do mês vigente
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [periodoFim, setPeriodoFim] = useState(() => {
    // Termina no último dia do mês vigente
    const date = new Date();
    const ultimoDia = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(ultimoDia).padStart(2, '0')}`;
  });
  const [categoriasFiltro, setCategoriasFiltro] = useState('todas');
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/transacoes');
      setTransacoes(data);
      processarRelatorioMensal(data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const processarRelatorioMensal = (dados) => {
    const meses = {};
    
    dados.forEach(t => {
      const data = new Date(t.data);
      const mesAno = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
      
      if (!meses[mesAno]) {
        meses[mesAno] = { mes: mesAno, entradas: 0, saidas: 0, saldo: 0 };
      }
      
      if (t.tipo === 'entrada') {
        meses[mesAno].entradas += t.valor;
      } else {
        meses[mesAno].saidas += t.valor;
      }
      meses[mesAno].saldo = meses[mesAno].entradas - meses[mesAno].saidas;
    });

    const resultado = Object.values(meses)
      .sort((a, b) => a.mes.localeCompare(b.mes))
      .map(m => ({
        ...m,
        mesFormatado: formatarMes(m.mes)
      }));

    setRelatorioMensal(resultado);
  };

  const formatarMes = (mesAno) => {
    const [ano, mes] = mesAno.split('-');
    const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${meses[parseInt(mes) - 1]}/${ano.slice(2)}`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  // Filtrar transações por período
  const transacoesFiltradas = transacoes.filter(t => {
    const dataTransacao = new Date(t.data);
    const inicio = new Date(periodoInicio);
    const fim = new Date(periodoFim);
    fim.setHours(23, 59, 59);
    
    const dentroPerido = dataTransacao >= inicio && dataTransacao <= fim;
    const dentroCategoria = categoriasFiltro === 'todas' || t.categoria.toLowerCase() === categoriasFiltro.toLowerCase();
    
    return dentroPerido && dentroCategoria;
  });

  // Calcular totais do período filtrado
  const totaisPeriodo = transacoesFiltradas.reduce((acc, t) => {
    if (t.tipo === 'entrada') {
      acc.entradas += t.valor;
    } else {
      acc.saidas += t.valor;
    }
    acc.saldo = acc.entradas - acc.saidas;
    return acc;
  }, { entradas: 0, saidas: 0, saldo: 0 });

  // Agrupar por categoria
  const porCategoria = transacoesFiltradas.reduce((acc, t) => {
    const cat = t.categoria || 'Outros';
    if (!acc[cat]) {
      acc[cat] = { categoria: cat, entradas: 0, saidas: 0, total: 0 };
    }
    if (t.tipo === 'entrada') {
      acc[cat].entradas += t.valor;
    } else {
      acc[cat].saidas += t.valor;
    }
    acc[cat].total = acc[cat].entradas - acc[cat].saidas;
    return acc;
  }, {});

  const categoriasData = Object.values(porCategoria).sort((a, b) => b.entradas - a.entradas);

  // Dados para gráfico de pizza
  const pizzaData = [
    { name: 'Entradas', value: totaisPeriodo.entradas, color: '#22C55E' },
    { name: 'Saídas', value: totaisPeriodo.saidas, color: '#EF4444' }
  ];

  // Categorias únicas
  const categoriasUnicas = [...new Set(transacoes.map(t => t.categoria))].filter(Boolean);

  // Exportar relatório em PDF
  const exportarRelatorioPDF = async () => {
    setExportingPdf(true);
    try {
      const response = await api.post('/contabilidade/relatorio-pdf', {
        periodo_inicio: periodoInicio,
        periodo_fim: periodoFim,
        categoria: categoriasFiltro,
        totais: totaisPeriodo,
        transacoes: transacoesFiltradas.map(t => ({
          data: t.data,
          tipo: t.tipo,
          categoria: t.categoria || '',
          descricao: t.descricao || '',
          valor: t.valor,
          status: t.status
        }))
      }, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio_contabil_${periodoInicio}_${periodoFim}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Relatório PDF exportado!');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
      console.error(error);
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="contabilidade-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 lg:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white uppercase tracking-tight mb-1">Contabilidade</h1>
          <p className="text-sm text-slate-500">Relatórios e análises financeiras</p>
        </div>
        <Button 
          onClick={exportarRelatorioPDF}
          disabled={exportingPdf}
          className="bg-electric-blue hover:bg-blue-600 text-white rounded-sm w-full sm:w-auto"
        >
          <Download className="w-4 h-4 mr-2" />
          {exportingPdf ? 'Gerando...' : 'Exportar PDF'}
        </Button>
      </div>

      {/* Filtros */}
      <div className="bg-carbon border border-white/5 rounded-md p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-electric-blue" />
          <span className="text-sm font-medium text-white uppercase tracking-wider">Filtros</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Data Início</Label>
            <Input
              type="date"
              value={periodoInicio}
              onChange={(e) => setPeriodoInicio(e.target.value)}
              className="bg-obsidian border-white/10 text-white"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Data Fim</Label>
            <Input
              type="date"
              value={periodoFim}
              onChange={(e) => setPeriodoFim(e.target.value)}
              className="bg-obsidian border-white/10 text-white"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Categoria</Label>
            <Select value={categoriasFiltro} onValueChange={setCategoriasFiltro}>
              <SelectTrigger className="bg-obsidian border-white/10 text-white">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent className="bg-carbon border-white/10 text-white">
                <SelectItem value="todas">Todas</SelectItem>
                {categoriasUnicas.map(cat => (
                  <SelectItem key={cat} value={cat.toLowerCase()}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Cards de Resumo do Período */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-carbon border border-white/5 rounded-md p-3 sm:p-4 lg:p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Entradas</p>
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
          </div>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-500">{formatCurrency(totaisPeriodo.entradas)}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-carbon border border-white/5 rounded-md p-3 sm:p-4 lg:p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Saídas</p>
            <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
          </div>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-red-500">{formatCurrency(totaisPeriodo.saidas)}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-carbon border border-white/5 rounded-md p-3 sm:p-4 lg:p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Lucro/Prejuízo</p>
            <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-electric-blue" />
          </div>
          <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${totaisPeriodo.saldo >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {formatCurrency(totaisPeriodo.saldo)}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-carbon border border-white/5 rounded-md p-3 sm:p-4 lg:p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Transações</p>
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
          </div>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-white">{transacoesFiltradas.length}</p>
        </motion.div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mb-6">
        {/* Gráfico de Evolução Mensal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-carbon border border-white/5 rounded-md p-4 sm:p-6"
        >
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4 uppercase tracking-wide flex items-center gap-2">
            <Calendar className="w-5 h-5 text-electric-blue" />
            Evolução Mensal
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={relatorioMensal}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2D333E" />
              <XAxis dataKey="mesFormatado" stroke="#64748B" tick={{ fontSize: 11 }} />
              <YAxis stroke="#64748B" tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#16191E',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  color: '#F8FAFC'
                }}
                formatter={(value) => formatCurrency(value)}
              />
              <Bar dataKey="entradas" fill="#22C55E" name="Entradas" radius={[4, 4, 0, 0]} />
              <Bar dataKey="saidas" fill="#EF4444" name="Saídas" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Gráfico de Saldo Mensal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-carbon border border-white/5 rounded-md p-4 sm:p-6"
        >
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4 uppercase tracking-wide flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-electric-blue" />
            Evolução do Lucro
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={relatorioMensal}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2D333E" />
              <XAxis dataKey="mesFormatado" stroke="#64748B" tick={{ fontSize: 11 }} />
              <YAxis stroke="#64748B" tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#16191E',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  color: '#F8FAFC'
                }}
                formatter={(value) => formatCurrency(value)}
              />
              <Line type="monotone" dataKey="saldo" stroke="#3B82F6" strokeWidth={3} dot={{ fill: '#3B82F6', strokeWidth: 2 }} name="Lucro" />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Distribuição e Categorias */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Gráfico de Pizza */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-carbon border border-white/5 rounded-md p-4 sm:p-6"
        >
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4 uppercase tracking-wide">Distribuição</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={pizzaData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {pizzaData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#16191E',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  color: '#F8FAFC'
                }}
                formatter={(value) => formatCurrency(value)}
              />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Tabela por Categoria */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="lg:col-span-2 bg-carbon border border-white/5 rounded-md p-4 sm:p-6"
        >
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4 uppercase tracking-wide">Por Categoria</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 text-xs uppercase tracking-wider text-slate-500">Categoria</th>
                  <th className="text-right py-2 text-xs uppercase tracking-wider text-slate-500">Entradas</th>
                  <th className="text-right py-2 text-xs uppercase tracking-wider text-slate-500">Saídas</th>
                  <th className="text-right py-2 text-xs uppercase tracking-wider text-slate-500">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {categoriasData.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="py-8 text-center text-slate-500">
                      Nenhuma transação no período
                    </td>
                  </tr>
                ) : (
                  categoriasData.map((cat, index) => (
                    <tr key={cat.categoria || `cat-${index}`} className="border-b border-white/5">
                      <td className="py-3 text-sm text-white">{cat.categoria}</td>
                      <td className="py-3 text-sm text-right text-green-500">{formatCurrency(cat.entradas)}</td>
                      <td className="py-3 text-sm text-right text-red-500">{formatCurrency(cat.saidas)}</td>
                      <td className={`py-3 text-sm text-right font-semibold ${cat.total >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {formatCurrency(cat.total)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {categoriasData.length > 0 && (
                <tfoot>
                  <tr className="border-t border-white/20">
                    <td className="py-3 text-sm font-bold text-white">TOTAL</td>
                    <td className="py-3 text-sm text-right font-bold text-green-500">{formatCurrency(totaisPeriodo.entradas)}</td>
                    <td className="py-3 text-sm text-right font-bold text-red-500">{formatCurrency(totaisPeriodo.saidas)}</td>
                    <td className={`py-3 text-sm text-right font-bold ${totaisPeriodo.saldo >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {formatCurrency(totaisPeriodo.saldo)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default Contabilidade;
